import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { Upload, Camera, Search, Sparkles, ArrowRight, Ruler } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export function VisualProofSection() {
  const navigate = useNavigate();
  return <section className="py-24 bg-card relative overflow-hidden" data-id="element-146">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-147">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/50 rounded-full blur-3xl -z-10" data-id="element-148" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-149">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-150">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-151">
            See It On You{' '}
            <span className="italic text-accent" data-id="element-152">Before You Buy</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-text-muted leading-relaxed" data-id="element-153">
            Our AI instantly visualizes how any dress will fit your exact
            proportions—no more guessing games.
          </motion.p>
        </div>

        {/* Visual Flow */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 mb-16 relative" data-id="element-154">
          {/* Connecting Arrows (Desktop) */}
          <div className="hidden md:block absolute top-1/2 left-1/3 -translate-y-1/2 -translate-x-1/2 z-20 text-accent/30" data-id="element-155">
            <ArrowRight className="w-8 h-8" data-id="element-156" />
          </div>
          <div className="hidden md:block absolute top-1/2 left-2/3 -translate-y-1/2 -translate-x-1/2 z-20 text-accent/30" data-id="element-157">
            <ArrowRight className="w-8 h-8" data-id="element-158" />
          </div>

          {/* Step 1: Upload */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-primary rounded-2xl p-6 border border-border shadow-lg relative group hover:shadow-xl hover:border-accent/30 transition-all duration-300" data-id="element-159">
            <div className="absolute -top-4 left-6 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-160">
              Step 1
            </div>

            {/* Visual Mockup */}
            <div className="bg-card rounded-xl p-6 mb-6 aspect-[4/3] flex flex-col items-center justify-center border border-gray-100" data-id="element-161">
              <div className="w-full h-32 border-2 border-dashed border-accent/30 rounded-lg flex flex-col items-center justify-center bg-primary/30 mb-4 group-hover:border-accent/60 transition-colors" data-id="element-162">
                <Camera className="w-8 h-8 text-accent mb-2" data-id="element-163" />
                <span className="text-xs text-text-muted font-medium" data-id="element-164">
                  Upload Photo
                </span>
              </div>
              <div className="w-full space-y-2" data-id="element-165">
                <div className="flex items-center space-x-2" data-id="element-166">
                  <Ruler className="w-3 h-3 text-text-muted" data-id="element-167" />
                  <div className="h-2 bg-gray-100 rounded w-full" data-id="element-168"></div>
                </div>
                <div className="h-2 bg-gray-100 rounded w-2/3 ml-5" data-id="element-169"></div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-2" data-id="element-170">Upload Photo</h3>
            <p className="text-text-muted text-sm" data-id="element-171">
              Upload a quick body photo and enter a few key measurements.
            </p>
          </motion.div>

          {/* Step 2: Choose */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="bg-primary rounded-2xl p-6 border border-gray-100 shadow-sm relative group hover:shadow-md transition-shadow duration-300" data-id="element-172">
            <div className="absolute -top-4 left-6 bg-text-primary text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-173">
              Step 2
            </div>

            {/* Visual Mockup */}
            <div className="bg-white rounded-xl p-6 mb-6 aspect-[4/3] flex flex-col border border-gray-100 overflow-hidden relative" data-id="element-174">
              <div className="flex items-center space-x-2 mb-4 border-b border-gray-100 pb-3" data-id="element-175">
                <Search className="w-4 h-4 text-text-muted" data-id="element-176" />
                <div className="text-xs text-text-primary font-medium" data-id="element-177">
                  Ann Taylor Petite...
                </div>
              </div>
              <div className="space-y-3" data-id="element-178">
                <div className="flex items-center space-x-3 p-2 rounded bg-accent/10 border border-accent/20" data-id="element-179">
                  <div className="w-8 h-8 bg-white rounded-full border border-gray-100 flex-shrink-0" data-id="element-180"></div>
                  <div className="flex-1" data-id="element-181">
                    <div className="h-2 bg-text-primary/80 rounded w-20 mb-1" data-id="element-182"></div>
                    <div className="h-1.5 bg-text-muted/50 rounded w-12" data-id="element-183"></div>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-2 rounded opacity-50" data-id="element-184">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex-shrink-0" data-id="element-185"></div>
                  <div className="flex-1" data-id="element-186">
                    <div className="h-2 bg-gray-200 rounded w-16 mb-1" data-id="element-187"></div>
                    <div className="h-1.5 bg-gray-100 rounded w-10" data-id="element-188"></div>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-text-primary mb-2 font-serif" data-id="element-189">
              Choose Brand & Item
            </h3>
            <p className="text-text-muted text-sm" data-id="element-190">
              Select from supported brands like Ann Taylor, J.Crew, or Banana
              Republic.
            </p>
          </motion.div>

          {/* Step 3: Visualize */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.6
        }} className="bg-primary rounded-2xl p-6 border border-gray-100 shadow-sm relative group hover:shadow-md transition-shadow duration-300" data-id="element-191">
            <div className="absolute -top-4 left-6 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider flex items-center gap-1" data-id="element-192">
              <Sparkles className="w-3 h-3" data-id="element-193" />
              Step 3
            </div>

            {/* Visual Mockup */}
            <div className="bg-white rounded-xl mb-6 aspect-[4/3] flex items-center justify-center border border-gray-100 relative overflow-hidden" data-id="element-194">
              {/* Abstract Body Silhouette */}
              <div className="relative h-32 w-24" data-id="element-195">
                {/* Body Shape */}
                <div className="absolute inset-0 bg-gray-100 rounded-full opacity-50 mx-auto w-16" data-id="element-196"></div>
                {/* Dress Overlay */}
                <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.8,
                duration: 0.8
              }} className="absolute inset-0 z-10" data-id="element-197">
                  <svg viewBox="0 0 100 120" className="w-full h-full fill-accent drop-shadow-lg" data-id="element-198">
                    <path d="M30,10 Q50,25 70,10 L80,20 L75,60 L90,110 L10,110 L25,60 L20,20 Z" data-id="element-199" />
                  </svg>
                </motion.div>

                {/* AI Badge */}
                <div className="absolute -bottom-2 -right-4 bg-text-primary text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm z-20" data-id="element-200">
                  <Sparkles className="w-2 h-2 text-accent" data-id="element-201" />
                  AI Match
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-text-primary mb-2 font-serif" data-id="element-202">
              See Your Perfect Fit
            </h3>
            <p className="text-text-muted text-sm" data-id="element-203">
              Instantly see how the item fits your specific body shape and
              height.
            </p>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.8
      }} className="text-center" data-id="element-204">
          <Button size="lg" className="shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300" onClick={() => navigate('/try-it')} data-id="element-205">
            Try It Now
          </Button>
        </motion.div>
      </div>
    </section>;
}